"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ToolUIPart } from "ai";
import type { ComponentProps, KeyboardEvent, ReactNode } from "react";
import { createContext, useContext, useMemo, useState } from "react";

type ToolUIPartApproval = ToolUIPart["approval"];

interface QuestionContextValue {
  approval: ToolUIPartApproval;
  state: ToolUIPart["state"];
}

const QuestionContext = createContext<QuestionContextValue | null>(null);

const useQuestion = () => {
  const context = useContext(QuestionContext);

  if (!context) {
    throw new Error("Question components must be used within Question");
  }

  return context;
};

export type QuestionProps = ComponentProps<typeof Alert> & {
  approval?: ToolUIPartApproval;
  state: ToolUIPart["state"];
};

export const Question = ({ className, approval, state, ...props }: QuestionProps) => {
  const contextValue = useMemo(() => ({ approval, state }), [approval, state]);

  if (!approval || state === "input-streaming" || state === "input-available") {
    return null;
  }

  return (
    <QuestionContext.Provider value={contextValue}>
      <Alert className={cn("flex flex-col gap-3", className)} {...props} />
    </QuestionContext.Provider>
  );
};

export type QuestionTitleProps = ComponentProps<typeof AlertDescription>;

export const QuestionTitle = ({ className, ...props }: QuestionTitleProps) => (
  <AlertDescription className={cn("inline", className)} {...props} />
);

export interface QuestionRequestProps {
  children?: ReactNode;
}

export const QuestionRequest = ({ children }: QuestionRequestProps) => {
  const { state } = useQuestion();

  if (state !== "approval-requested") {
    return null;
  }

  return children;
};

export interface QuestionAnsweredProps {
  children?: ReactNode;
}

export const QuestionAnswered = ({ children }: QuestionAnsweredProps) => {
  const { state } = useQuestion();

  if (
    state !== "approval-responded" &&
    state !== "output-denied" &&
    state !== "output-available"
  ) {
    return null;
  }

  return children;
};

// A compact, divided list of full-width rows (question, hint, "Something
// else") — deliberately dense rather than pill buttons with descriptions
// stacked underneath, to match Claude's own AskUserQuestion list density.
export type QuestionOptionsProps = ComponentProps<"div">;

export const QuestionOptions = ({ className, ...props }: QuestionOptionsProps) => (
  <div
    className={cn(
      "flex flex-col divide-y divide-border overflow-hidden rounded-lg border border-border",
      className
    )}
    {...props}
  />
);

export type QuestionOptionProps = Omit<ComponentProps<"button">, "onClick"> & {
  label: string;
  description?: string | null;
  onClick?: (label: string) => void;
};

export const QuestionOption = ({
  label,
  description,
  className,
  onClick,
  ...props
}: QuestionOptionProps) => (
  <button
    className={cn(
      "flex w-full items-baseline gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent/50",
      className
    )}
    onClick={() => onClick?.(label)}
    type="button"
    {...props}
  >
    <span className="font-medium">{label}</span>
    {description && (
      <span className="min-w-0 truncate text-xs text-muted-foreground">{description}</span>
    )}
  </button>
);

export interface QuestionOtherProps {
  onSubmit: (value: string) => void;
  label?: string;
  placeholder?: string;
}

export const QuestionOther = ({
  onSubmit,
  label = "Something else",
  placeholder = "Type your own answer…",
}: QuestionOtherProps) => {
  const { state } = useQuestion();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState("");

  if (state !== "approval-requested") {
    return null;
  }

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  if (!isEditing) {
    return (
      <button
        className="flex w-full items-center px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent/50"
        onClick={() => setIsEditing(true)}
        type="button"
      >
        {label}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <Input
        autoFocus
        className="h-8 text-sm"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        value={value}
      />
      <Button
        className="h-8 px-3 text-sm"
        disabled={!value.trim()}
        onClick={submit}
        size="sm"
        type="button"
      >
        Send
      </Button>
    </div>
  );
};

// ── Answer encoding ──────────────────────────────────────────────────────────
// AI SDK v7's tool-approval-response only ever forwards a model-visible
// tool-result on the *denied* path — approved:true silently re-invokes
// execute with no way to pass back which option was picked. So this tool
// always resolves approved:false, and the actual answer is packed into the
// reason string below (mirrors RegisterCreateConfirmation's approach in
// chat-view.tsx). parseClarifyingAnswerReason lets a reloaded chat re-render
// the concluded state from persisted history instead of the live pills.

export type ClarifyingAnswerKind = "selected" | "custom" | "dismissed";

const ANSWERED_PREFIX = "Clarifying question answered.";
const DISMISSED_PREFIX = "Clarifying question dismissed.";

export const buildClarifyingAnswerReason = ({
  question,
  kind,
  answer,
}: {
  question: string;
  kind: ClarifyingAnswerKind;
  answer?: string;
}): string => {
  if (kind === "dismissed") {
    return `${DISMISSED_PREFIX} Question: "${question}". The user did not answer directly and moved on — proceed with your best judgement and do not ask again.`;
  }
  if (kind === "custom") {
    return `${ANSWERED_PREFIX} Question: "${question}". User typed a custom answer: "${answer ?? ""}". Treat this as the user's decision, do not ask again, and continue the task.`;
  }
  return `${ANSWERED_PREFIX} Question: "${question}". User selected: "${answer ?? ""}". Treat this as the user's decision, do not ask again, and continue the task.`;
};

export const parseClarifyingAnswerReason = (
  reason: string | undefined
): { kind: ClarifyingAnswerKind; answer: string | null } | null => {
  if (!reason) return null;
  if (reason.startsWith(DISMISSED_PREFIX)) {
    return { kind: "dismissed", answer: null };
  }
  if (!reason.startsWith(ANSWERED_PREFIX)) {
    return null;
  }
  const customMatch = reason.match(/User typed a custom answer: "(.*)"\. Treat/);
  if (customMatch) {
    return { kind: "custom", answer: customMatch[1] };
  }
  const selectedMatch = reason.match(/User selected: "(.*)"\. Treat/);
  if (selectedMatch) {
    return { kind: "selected", answer: selectedMatch[1] };
  }
  return { kind: "selected", answer: null };
};
